import React, { useEffect, useState } from "react";
import { Building2, Clock3, Users, CircleDollarSign } from "lucide-react";
import axios from "axios";

import AdminLayout from "../components/AdminLayout";
import { BASE_URL } from "../../../config/config";
import { useNavigate } from "react-router-dom";

const StatCard = ({ title, value, subtitle, icon: Icon, className = "" }) => {
  return (
    <div
      className={`rounded-[8px] border border-[#e0e4e8] bg-white p-4 ${className}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[12px] text-[#71808c]">{title}</p>

          <p className="mt-2 text-[18px] font-semibold text-[#162b3a]">
            {value}
          </p>

          <p className="mt-1 text-[10px] text-[#9ba5ad]">{subtitle}</p>
        </div>

        <Icon size={13} strokeWidth={1.6} className="text-[#6c7782]" />
      </div>
    </div>
  );
};

const AdminHome = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate()

  const getDashboardData = async () => {
    setIsLoading(true);

    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.get(`${BASE_URL}/admin/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Dashboard API response:", response);

      if (response?.data) {
        setDashboardData(response.data);
      }
    } catch (error) {
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      console.log("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  const formatAmount = (amount) => {
    const value = Number(amount || 0);

    if (value >= 1000000) {
      return `AED ${(value / 1000000).toFixed(1)}M`;
    }

    if (value >= 1000) {
      return `AED ${(value / 1000).toFixed(1)}K`;
    }

    return `AED ${value.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getFundingPercentage = (property) => {
    if (property?.funding_pct !== undefined) {
      return Math.min(Number(property.funding_pct || 0), 100);
    }

    if (property?.total_tokens > 0) {
      return Math.min(
        (property.blocks_sold / property?.total_tokens) * 100,
        100,
      );
    }

    return 0;
  };

  const totalInvestors = dashboardData?.total_investors || 0;
  const verifiedInvestors = dashboardData?.verified_investors || 0;

  return (
    <AdminLayout title="Dashboard">
      <div className="mb-5">
        <h1 className="text-[17px] font-semibold text-[#142b3b]">Dashboard</h1>

        <p className="mt-1 text-[9px] text-[#84909b]">
          Overview of all platform activity.
        </p>
      </div>

      {isLoading && !dashboardData ? (
        <div className="rounded-[8px] border border-[#e0e4e8] bg-white px-4 py-10 text-center">
          <p className="text-[11px] text-[#84909b]">Loading dashboard...</p>
        </div>
      ) : (
        <>
          {/* Top Stats */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Properties"
              value={dashboardData?.total_properties ?? 0}
              subtitle={`${dashboardData?.published_properties ?? 0} published`}
              icon={Building2}
            />

            <StatCard
              title="Pending Review"
              value={dashboardData?.pending_property_submissions ?? 0}
              subtitle="Needs action"
              icon={Clock3}
              className="bg-[#fffbed]"
            />

            <StatCard
              title="Active Investors"
              value={dashboardData?.verified_investors ?? 0}
              subtitle={`of ${totalInvestors} total`}
              icon={Users}
              className="bg-[#f0f6ff]"
            />

            <StatCard
              title="Total Raised"
              value={formatAmount(dashboardData?.total_investment_aed)}
              subtitle={`${dashboardData?.total_blocks_sold ?? 0} tokens sold`}
              icon={CircleDollarSign}
              className="bg-[#effbf3]"
            />
          </div>

          {/* Funding + Transactions */}
          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
            {/* Funding Progress */}
            <div className="rounded-[8px] border border-[#e0e4e8] bg-white p-4">
              <h2 className="text-[15px] font-semibold text-[#1b3444]">
                Funding Progress
              </h2>

              {dashboardData?.top_properties?.length ? (
                <div className="mt-5 space-y-5">
                  {dashboardData.top_properties.map((property) => {
                    const percentage = getFundingPercentage(property);

                    return (
                      <FundingItem
                        key={property.property_id}
                        name={property.property_name}
                        percentage={percentage}
                        blocksSold={property.blocks_sold}
                        totalBlocks={property?.total_tokens}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="py-10 text-center">
                  <p className="text-[10px] text-[#84909b]">
                    No property funding data available.
                  </p>
                </div>
              )}
            </div>

            {/* Recent Transactions */}
            <div className="rounded-[8px] border border-[#e0e4e8] bg-white p-4">
              <h2 className="text-[15px] font-semibold text-[#1b3444]">
                Recent Transactions
              </h2>

              <div className="mt-3 divide-y divide-[#edf0f2]">
                {dashboardData?.recent_transactions?.length ? (
                  dashboardData.recent_transactions.map((transaction) => (
                    <Transaction
                      key={transaction.id}
                      name={transaction.property_name}
                      investor={transaction.investor_name}
                      date={transaction.created_at}
                      blocks={transaction.blocks_purchased}
                      amount={transaction.price_paid_aed}
                    />
                  ))
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-[10px] text-[#84909b]">
                      No recent transactions found.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <BottomStat
              title="Verified Investors"
              value={`${verifiedInvestors} / ${totalInvestors}`}
            />

            <BottomStat
              title="Total Owners"
              value={dashboardData?.total_owners ?? 0}
            />

            <BottomStat
              title="Total Rent Distributed"
              value={formatAmount(dashboardData?.total_rent_distributed_aed)}
            />
          </div>
        </>
      )}
    </AdminLayout>
  );
};

const FundingItem = ({ name, percentage, blocksSold, totalBlocks }) => {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-[#285ce7]">{name}</span>

        <span className="text-[8px] text-[#73808a]">
          {percentage.toFixed(1)}%
        </span>
      </div>

      <div className="mt-1.5 h-[4px] overflow-hidden rounded-full bg-[#edf0f3]">
        <div
          className={`h-full rounded-full ${
            percentage >= 100 ? "bg-[#18a957]" : "bg-[#2c65ed]"
          }`}
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>

      <div className="mt-1 flex justify-between text-[7px] text-[#9aa4ad]">
        <span>{blocksSold ?? 0} tokens sold</span>

        <span>{totalBlocks ?? 0} total tokens</span>
      </div>
    </div>
  );
};

const Transaction = ({ name, investor, date, blocks, amount }) => {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 rounded-full bg-[#d9f8e4] px-1.5 py-[3px] text-[10px] font-medium text-[#159447]">
          Purchase
        </span>

        <div className="min-w-0">
          <p className="truncate text-[12px] font-medium text-[#253846]">
            {name || "-"}
          </p>

          <p className="truncate text-[10px] text-[#9aa4ad]">
            {investor || "-"} · {formatTransactionDate(date)}
          </p>

          <p className="text-[9px] text-[#9aa4ad]">{blocks ?? 0} tokens</p>
        </div>
      </div>

      <span className="ml-3 shrink-0 text-[10px] font-medium text-[#243846]">
        AED {Number(amount || 0).toLocaleString()}
      </span>
    </div>
  );
};

const formatTransactionDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const BottomStat = ({ title, value }) => {
  return (
    <div className="flex items-center justify-between rounded-[8px] border border-[#e0e4e8] bg-white px-4 py-3">
      <span className="text-[12px] text-[#71808c]">{title}</span>

      <span className="text-[15px] font-semibold text-[#142b3b]">{value}</span>
    </div>
  );
};

export default AdminHome;
